import {
  computed,
  nextTick,
  onMounted,
  readonly,
  ref,
  watch,
  type Ref,
} from "vue";

export type ChangePayload = { value: Date | null; event: any };

export function useMaskedDateInput(args: {
  id: string;
  onChange: (p: ChangePayload) => void;
  onShowCalendar: (e: KeyboardEvent) => void;
  externalValue?: Ref<string | Date | null | undefined>;
  min?: Date | null | undefined;
  max?: Date | null | undefined;
  /** When true, empty/incomplete value is considered invalid after blur */
  required?: boolean;
  /** Optional default date for this field (e.g., today). */
  defaultValue?: Date | null;
  dateFormat?: string;
  debug?: boolean;
}) {
  const raw = ref("");
  const cursorPos = ref<number | undefined>(undefined);
  const debugEnabled = ref<boolean>(!!args.debug);
  const placeholder = ref<string>(args.dateFormat ?? "mm/dd/yyyy");
  const isRequired = computed(() => !!args.required);
  const MIN_YEAR = 1000;
  const isTouched = ref(false);

  const steppers: string[] = ["ArrowUp", "ArrowDown"];
  const cursorKeys: string[] = ["ArrowLeft", "ArrowRight"];

  const digitsOnly = computed(() => (raw.value ?? "").replace(/\D/g, ""));

  const datePart = (pos: number) => {
    if (pos <= 2) return "mm";
    if (pos <= 5) return "dd";
    if (digitsOnly.value.length <= 10) return "yyyy";
    return "mm";
  };

  const month = computed(() => {
    const m = raw.value
      ? raw.value.substring(0, 2).replace(/\D/g, "0") || "0"
      : "0";
    return parseInt(m).toString().padStart(2, "0");
  });
  const day = computed(() => {
    const d = raw.value
      ? raw.value.substring(3, 5).replace(/\D/g, "0") || "0"
      : "0";
    return parseInt(d).toString().padStart(2, "0");
  });
  const year = computed(() => {
    const y = raw.value
      ? raw.value.substring(6, 10).replace(/\D/g, "0") || "0"
      : "0";
    return parseInt(y).toString().padStart(4, "0");
  });

  const emitParsedIfComplete = (event: any) => {
    if (digitsOnly.value.length === 0) {
      args.onChange({ value: null, event });
      return;
    }
    if (digitsOnly.value.length < 8) {
      args.onChange({ value: null, event });
      return;
    }
    if ((raw.value ?? "").length === 10 && digitsOnly.value.length === 8) {
      const [mmStr, ddStr, yyyyStr] = raw.value.split("/");
      const mm = parseInt(mmStr);
      const dd = parseInt(ddStr);
      const yyyy = parseInt(yyyyStr);
      // Prevent auto-correction: only emit if parts are a real calendar date
      if (isValidParts(yyyy, mm, dd)) {
        const d = createDate(yyyy, mm, dd);
        if (inRange(d)) {
          args.onChange({ value: d, event });
        } else {
          args.onChange({ value: null, event });
        }
      } else {
        args.onChange({ value: null, event });
      }
    }
  };
  const inRange = (d: Date) => {
    const min = args.min ?? undefined;
    const max = args.max ?? undefined;
    if (min && d < min) return false;
    if (max && d > max) return false;
    return true;
  };

  const parsedRawDate = computed<Date | null>(() => {
    if ((raw.value ?? "").length === 10 && digitsOnly.value.length === 8) {
      const [mmStr, ddStr, yyyyStr] = (raw.value ?? "").split("/");
      const mm = parseInt(mmStr);
      const dd = parseInt(ddStr);
      const yyyy = parseInt(yyyyStr);
      if (isValidParts(yyyy, mm, dd)) {
        return createDate(yyyy, mm, dd);
      }
    }
    return null;
  });

  const isComplete = computed(() => {
    return (raw.value ?? "").length === 10 && digitsOnly.value.length === 8;
  });

  // Helpers to centralize validity checks and baseline selection
  const createDate = (yy: number, mm: number, dd: number) => {
    const date = new Date(0);
    date.setFullYear(yy, mm - 1, dd);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const daysInMonth = (yy: number, mm: number) => {
    return createDate(yy, mm + 1, 0).getDate();
  };

  const isValidParts = (yy: number, mm: number, dd: number) => {
    return (
      mm >= 1 &&
      mm <= 12 &&
      yy >= MIN_YEAR &&
      dd >= 1 &&
      dd <= daysInMonth(yy, mm)
    );
  };

  const handleChange = (event: any): void => {
    raw.value = event.value as string;
    const src = event.event?.target as HTMLInputElement;
    cursorPos.value = src?.selectionStart ?? 0;
    emitParsedIfComplete(event);
  };

  const handleClick = (event: MouseEvent): void => {
    cursorPos.value = (event.target as HTMLInputElement).selectionStart ?? 0;
  };

  const applyStep = (
    direction: "ArrowUp" | "ArrowDown",
    sourceEl: HTMLInputElement | null | undefined,
    event: Event,
  ): void => {
    const pos = sourceEl?.selectionStart ?? cursorPos.value ?? 0;
    cursorPos.value = pos;
    let m = parseInt(month.value);
    let d = parseInt(day.value);
    let y = parseInt(year.value);

    // If input is invalid or incomplete, set to today's date and ignore the step
    const hasEightDigits = digitsOnly.value.length >= 8;
    if (!hasEightDigits || !isValidParts(y, m, d)) {
      const today = args.defaultValue ?? new Date();
      m = today.getMonth() + 1;
      d = today.getDate();
      y = today.getFullYear();
      raw.value = `${String(m).padStart(2, "0")}/${String(d).padStart(
        2,
        "0",
      )}/${String(y)}`;

      const setDate = createDate(y, m, d);
      args.onChange({ value: inRange(setDate) ? setDate : null, event });

      focusAndRestore({ useRAF: true });
      return;
    }

    const part = datePart(pos);

    if (part === "mm") {
      m = direction === "ArrowUp" ? (m < 12 ? m + 1 : 1) : m > 1 ? m - 1 : 12;
    } else if (part === "dd") {
      const days = new Date(y, m, 0).getDate();
      d =
        direction === "ArrowUp" ? (d < days ? d + 1 : 1) : d > 1 ? d - 1 : days;
    } else if (part === "yyyy") {
      y = direction === "ArrowUp" ? y + 1 : Math.max(1, y - 1);
    }

    raw.value = `${String(m).padStart(2, "0")}/${String(d).padStart(
      2,
      "0",
    )}/${String(y)}`;

    const [mmStr, ddStr, yyyyStr] = raw.value.split("/");
    const steppedDate = createDate(
      parseInt(yyyyStr),
      parseInt(mmStr),
      parseInt(ddStr),
    );
    if (
      steppedDate.toString() !== "Invalid Date" &&
      parseInt(yyyyStr) >= 1000
    ) {
      args.onChange({
        value: inRange(steppedDate) ? steppedDate : null,
        event,
      });
    } else {
      args.onChange({ value: null, event });
    }

    focusAndRestore({ useRAF: true });
  };

  const handleKeyDown = (event: KeyboardEvent): void => {
    if (event.code === "Space" || event.key === " ") {
      // Justified: Space opens the calendar popup instead of inserting a space.
      event.preventDefault();
      // Justified: Parent containers such as grid keyboard handlers should not consume this.
      event.stopPropagation();
      args.onShowCalendar(event);
      return;
    }

    // Alt+Down opens the menu; allow Alt combos to pass through
    if (event.key === "ArrowDown" && event.altKey) {
      // Justified: Alt+Down is reserved for opening the calendar popup.
      event.preventDefault();
      // Justified: Prevent parent keyboard navigation from treating this as row movement.
      event.stopPropagation();
      args.onShowCalendar(event);
      return;
    }

    if (steppers.includes(event.key)) {
      if (event.altKey) return;
      // Justified: ArrowUp/Down act as date-part steppers instead of native caret/page behavior.
      event.preventDefault();
      // Justified: Prevent parent containers such as the grid from also handling these arrows.
      event.stopPropagation();
      const src = event.target as HTMLInputElement;
      applyStep(event.key as "ArrowUp" | "ArrowDown", src, event);
      return;
    }
  };

  const handleWheel = (event: WheelEvent): void => {
    // Map mouse wheel to stepper behavior (up/down) based on caret segment
    // Prevent page scroll when interacting with the input
    event.preventDefault();
    const direction = event.deltaY < 0 ? "ArrowUp" : "ArrowDown";
    applyStep(direction, event.target as HTMLInputElement, event);
  };

  const handleKeyUp = (event: KeyboardEvent): void => {
    const src = event.target as HTMLInputElement;
    if (cursorKeys.includes(event.key)) {
      // Do not prevent default for caret navigation
      cursorPos.value = src?.selectionStart ?? 0;
      return;
    }
    if (steppers.includes(event.key)) {
      if (event.altKey) return;
      cursorPos.value = src?.selectionStart ?? 0;
    }
  };

  const handleBlur = (event: FocusEvent): void => {
    isTouched.value = true;

    // If required, no default provided, and value is still undefined/blank,
    // coerce external value from undefined -> "" so downstream validation
    // treats this as an incomplete required field.
    if (
      isRequired.value &&
      !args.defaultValue &&
      args.externalValue &&
      args.externalValue.value === undefined &&
      digitsOnly.value.length === 0
    ) {
      (args.externalValue as Ref<any>).value = "";
    }

    // Also ensure caret position is captured on blur
    const src = event.target as HTMLInputElement | null;
    if (src) {
      cursorPos.value = src.selectionStart ?? 0;
    }
  };

  // Centralized focus + caret restore helper to avoid duplicated logic.
  const focusAndRestore = (opts?: {
    /** milliseconds to delay the focus (useful to wait for popups to close) */
    delay?: number;
    /** when true, use requestAnimationFrame to schedule the focus */
    useRAF?: boolean;
    /** explicit caret position override; otherwise uses `cursorPos` or end */
    pos?: number | undefined;
  }) => {
    const run = () => {
      const inputEl = document.getElementById(
        args.id,
      ) as HTMLInputElement | null;
      if (!inputEl) return;
      const p = opts?.pos ?? cursorPos.value ?? inputEl.value.length;
      try {
        inputEl.focus();
        inputEl.setSelectionRange(p, p);
      } catch {
        // ignore if element not focusable or selection not supported
      }
    };

    nextTick(() => {
      if (opts?.delay && opts.delay > 0) {
        setTimeout(run, opts.delay);
        return;
      }
      if (opts?.useRAF) {
        requestAnimationFrame(run);
        return;
      }
      run();
    });
  };

  // Sync external value into raw when provided
  if (args.externalValue) {
    watch(
      args.externalValue,
      (newVal) => {
        if (newVal) {
          const date = new Date(newVal as any);
          if (date.toString() !== "Invalid Date") {
            const m = (date.getMonth() + 1).toString().padStart(2, "0");
            const d = date.getDate().toString().padStart(2, "0");
            const y = date.getFullYear().toString();
            raw.value = `${m}/${d}/${y}`;

            // When external value is updated (e.g., user selects a date
            // from the calendar), refocus the input and restore caret.
            // Delay slightly to avoid Kendo calendar focus/DOM operations
            // overriding our focus call.
            focusAndRestore({ delay: 50 });
            return;
          }
        }
        // If external value is null/invalid, keep current raw as-is
      },
      { immediate: true },
    );
  }

  const debugLines = computed(() => {
    return [
      ["Date", `${month.value} / ${day.value} / ${year.value}`],
      ["Digits only", digitsOnly.value],
      [
        "min",
        args.min?.toLocaleDateString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }) ?? null,
      ],
      [
        "max",
        args.max?.toLocaleDateString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }) ?? null,
      ],
    ];
  });

  // Range validation based on provided min/max and current external value
  const isValid = computed(() => {
    // Optional field preserves existing forgiving behavior
    if (!isRequired.value) {
      if (!isComplete.value) return true;
      const candidate = parsedRawDate.value;
      if (!candidate) return false; // complete but invalid parts
      return inRange(candidate);
    }

    // Required field: do not flag invalid until after blur
    if (!isTouched.value) return true;

    const candidate = parsedRawDate.value;
    if (!isComplete.value) return false; // incomplete after blur
    if (!candidate) return false; // complete but invalid parts
    return inRange(candidate);
  });

  const reason = computed<string | undefined>(() => {
    // When not required, only report reasons for completed-but-bad values
    if (!isRequired.value) {
      if (!isComplete.value) return undefined;
      const candidate = parsedRawDate.value;
      if (!candidate) return "invalid-date";
      if (!inRange(candidate)) return "out-of-bounds";
      return "valid";
    }

    // Required: only validate after blur
    if (!isTouched.value) return undefined;

    if (digitsOnly.value.length === 0 || !isComplete.value) {
      return "incomplete";
    }

    const candidate = parsedRawDate.value;
    if (!candidate) return "invalid-date";
    if (!inRange(candidate)) return "out-of-bounds";
    return "valid";
  });

  const validationMessage = computed(() => {
    const r = reason.value;
    if (!r || r === "valid") return "";

    if (r === "incomplete") {
      return "Required";
    }

    if (r === "invalid-date") {
      return `Must be in ${placeholder.value} format.`;
    }

    const minStr = args.min
      ? args.min.toLocaleDateString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
      : null;
    const maxStr = args.max
      ? args.max.toLocaleDateString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
      : null;
    if (r === "out-of-bounds") {
      if (minStr && maxStr) return `Must be between ${minStr} and ${maxStr}.`;
      if (minStr) return `Must be on or after ${minStr}.`;
      if (maxStr) return `Must be on or before ${maxStr}.`;
    }

    return "";
  });

  const initStyling = () => {
    // Adds our `fk-datepicker` class to the parent of the Kendo datepicker
    // This is separated so callers can invoke styling on demand.
    nextTick(() => {
      const inputEl = document.getElementById(
        args.id,
      ) as HTMLInputElement | null;

      if (!inputEl) {
        console.warn(`ID (#${args.id}) not found for styling.`);
        return;
      }
      const datePicker = inputEl.closest(".k-datepicker");
      if (!datePicker) {
        console.warn(`.k-datepicker parent not found for #${args.id} styling.`);
        return;
      }

      // Add class to parent to avoid it being wiped away by Kendo's DOM manipulation
      const parent = datePicker.parentElement;
      if (parent) {
        // Idempotent: if we've already added our class, do nothing.
        if (parent.classList.contains("fk-datepicker")) {
          return;
        }
        parent.classList.add("fk-datepicker");
      } else {
        console.warn(
          `Parent of .k-datepicker not found for #${args.id} styling.`,
        );
      }
    });
  };

  onMounted(() => {
    initStyling();
  });

  return {
    raw,
    cursorPos,
    debugEnabled,
    debugLines,
    placeholder,
    isValid,
    reason: readonly(reason),
    validationMessage,
    digitsOnly,
    month: readonly(month),
    day: readonly(day),
    year: readonly(year),
    datePart,
    handleChange,
    handleKeyDown,
    handleWheel,
    handleKeyUp,
    handleClick,
    handleBlur,
    // Expose so consumers can re-run styling when needed
    initStyling,
  };
}
